import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { MailWarning, RefreshCw, Loader2, BellRing } from 'lucide-react';

const SIG_STYLE = {
  auth: 'bg-red-100 text-red-800',
  recipient: 'bg-amber-100 text-amber-800',
  connection: 'bg-sky-100 text-sky-800',
  other: 'bg-gray-100 text-gray-700',
};

/** Admin → Settings: recent outgoing-email failures + alert test. */
const EmailDeliveryPanel = ({ apiUrl }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/email-failures?limit=50`);
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { load(); }, [load]);

  const testAlert = async () => {
    setTesting(true); setMsg('');
    try {
      const r = await fetch(`${apiUrl}/api/admin/email-failures/test-alert`, { method: 'POST' });
      setMsg(r.ok ? `Simulated failure logged — an alert was sent to ${data?.alert_recipient || 'the alert address'} (unless one went out in the last 30 min).` : 'Test failed.');
      load();
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-white" data-testid="email-delivery-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <MailWarning className="h-5 w-5 text-[#0077B6]" />
          <h3 className="font-semibold text-gray-800">Email Delivery</h3>
          {data && (
            <span className="text-xs text-gray-500" data-testid="email-failure-counts">
              {data.last_24h} failure{data.last_24h === 1 ? '' : 's'} in 24h • {data.last_7d} in 7 days • alerts → {data.alert_recipient || '—'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="email-failures-refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          <Button size="sm" variant="outline" onClick={testAlert} disabled={testing} data-testid="email-failures-test-alert">
            {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <BellRing className="h-4 w-4 mr-1" />}Send test alert
          </Button>
        </div>
      </div>
      {msg && <p className="text-xs text-emerald-700 mb-2" data-testid="email-failures-test-msg">{msg}</p>}
      {loading && !data ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data?.rows?.length ? (
        <p className="text-sm text-gray-500" data-testid="email-failures-empty">No failed emails recorded.</p>
      ) : (
        <div className="overflow-x-auto max-h-72 overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-600 uppercase sticky top-0">
              <tr><th className="text-left px-2 py-1.5">When</th><th className="text-left px-2 py-1.5">To</th><th className="text-left px-2 py-1.5">Subject</th><th className="text-left px-2 py-1.5">Type</th><th className="text-left px-2 py-1.5">Error</th></tr>
            </thead>
            <tbody className="divide-y">
              {data.rows.map((r, i) => (
                <tr key={i} data-testid={`email-failure-row-${i}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-gray-800">{r.to}</td>
                  <td className="px-2 py-1.5 text-gray-700 max-w-[220px] truncate" title={r.subject}>{r.subject}</td>
                  <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded font-semibold ${SIG_STYLE[r.signature] || SIG_STYLE.other}`}>{r.signature}</span></td>
                  <td className="px-2 py-1.5 text-red-700 max-w-[320px] truncate" title={r.error}>{r.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmailDeliveryPanel;
